import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Button } from "@mui/material";
import * as React from "react";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../hooks/useSiteCore";
import { UsedByClient } from "../../pages/createClient/modelsAndSchemas/Models";
import { createClientSiteCoreKeys } from "../../pages/createClient/siteCore/SiteCoreKeys";
import { MuiBox } from "../box/MuiBox";
import { MuiButton } from "../button/MuiButton";
import MuiEllipseLabel from "../ellipseLabel/MuiEllipseLabel";
import { MuiPopup } from "../popup/MuiPopup";
import { CustomMenu } from "../select/MuiSelect";
import { MuiTypography } from "../typography/MuiTypography";
import { MenuWrapper, PopupBody } from "./MuiCustomMenu.style";

interface UsedByClientsProps {
  data: UsedByClient[] | CustomMenu;
  isLocale?: boolean;
}

const MuiCustomMenu: React.FC<UsedByClientsProps> = ({
  data,
  isLocale = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const [forceClose, setForceClose] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const { commonLabels, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleForceClose = () => setForceClose(!forceClose);

  const sortedData = React.useMemo(() => {
    if (isLocale) {
      return data as CustomMenu;
    }
    return (data as UsedByClient[]).sort(
      (a, b) => (b.isParent ? 1 : -1) - (a.isParent ? 1 : -1),
    );
  }, [data, isLocale]);

  return (
    <MenuWrapper>
      <MuiButton
        className={`meatball-menu ${open ? "active" : ""}`}
        variant="outlined"
        onClick={open ? handleClose : handleOpen}
        data-testid="meatball-menu"
        buttonType={open ? "btn-primary" : "btn-secondary"}
        ref={anchorRef}
        aria-label={commonARIALabel.showMoreDetails}
      >
        <MoreHorizIcon />
      </MuiButton>

      {open && (
        <MuiPopup
          open
          onClose={handleClose}
          refValue={anchorRef.current}
          forceClose={forceClose}
        >
          <PopupBody data-testid="custom-menu-popup">
            <MuiBox className="header">
              {isLocale ? (
                <MuiTypography variant="h2SemiBold">
                  {(data as CustomMenu).title}
                </MuiTypography>
              ) : (
                <MuiTypography variant="h2SemiBold">
                  {commonLabels.hierarchy}
                </MuiTypography>
              )}
              <Button
                className="close-icon"
                variant="text"
                onClick={handleForceClose}
                aria-label="Close"
              >
                <CloseIcon />
              </Button>
            </MuiBox>

            <MuiBox className="body">
              {isLocale ? (
                <>
                  {(data as CustomMenu).data.map((item, i) => (
                    <MuiBox className="client-card" key={i}>
                      <MuiBox className="full-width">
                        <MuiEllipseLabel
                          labelTypoVariant={"h3Bold"}
                          text={item.name}
                          labelTypoClassName={`card-title`}
                        />
                        <MuiBox>
                          <MuiTypography variant="h3">
                            {item.value}
                          </MuiTypography>
                        </MuiBox>
                      </MuiBox>
                    </MuiBox>
                  ))}
                </>
              ) : (
                (sortedData as UsedByClient[]).map((item) => (
                  <MuiBox className="client-card" key={item.clientId}>
                    <MuiBox
                      className={
                        item.isParent ? "client-title-wrapper" : "full-width"
                      }
                    >
                      <MuiEllipseLabel
                        labelTypoVariant={"h3Bold"}
                        text={`${item.clientName} - ${item.addressCountry}`}
                        labelTypoClassName={`card-title`}
                      />

                      <MuiBox>
                        <MuiTypography variant="h3">
                          {item.contactType
                            .map((type: string) =>
                              fetchValueByKeyOfDropdown(
                                createClientSiteCoreKeys.addContactTitle,
                                createClientSiteCoreKeys.contactTypeDropdown,
                                type,
                              ),
                            )
                            .join(", ")}
                        </MuiTypography>
                      </MuiBox>
                    </MuiBox>
                    {item.isParent && (
                      <MuiBox>
                        <MuiTypography
                          variant="h3Bold"
                          className="parent-active"
                        >
                          {commonLabels.parent}
                        </MuiTypography>
                      </MuiBox>
                    )}
                  </MuiBox>
                ))
              )}
            </MuiBox>
          </PopupBody>
        </MuiPopup>
      )}
    </MenuWrapper>
  );
};

export default MuiCustomMenu;
